import { useCallback, useRef, useState } from "react";
import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { TemplateDrawerTrail } from "@components/settings/ChartOfAccountsTemplates/TemplateDrawerTrail";
import { Button } from "@design-system/Button/Button";
import { Drawer } from "@design-system/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import {
    PlainModal,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { useChartOfAccountsTemplateAccounts } from "@api/coaTemplate.api";
import { ChartOfAccountsTemplateStepSubtitle } from "../ChartOfAccountsTemplateStepSubtitle";
import {
    ChartOfAccountsTemplateEditorContent,
    ChartOfAccountsTemplateEditorHandle,
} from "./ChartOfAccountsTemplateEditorContent";
import styles from "./ChartOfAccountsTemplateEditor.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface Props {
    template: ChartOfAccountsTemplate | null;
    isOpen: boolean;
    onClose: () => void;
}

export const ChartOfAccountsTemplateEditorDrawer = ({
    template,
    isOpen,
    onClose,
}: Props) => {
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // The blocked reason captured when the modal opened; drives the disabled
    // state and tooltip of "Save and close". Undefined means the draft is
    // savable.
    const [saveBlockedReason, setSaveBlockedReason] = useState<
        string | undefined
    >(undefined);
    const contentRef = useRef<ChartOfAccountsTemplateEditorHandle>(null);

    const { data } = useChartOfAccountsTemplateAccounts({
        templateId: template?.id,
        enabled: isOpen,
    });

    const handleCloseAttempt = useCallback((cancel: () => void) => {
        if (contentRef.current?.getIsDirty()) {
            cancel();
            setSaveBlockedReason(contentRef.current.getSaveBlockedReason());
            setShowCloseConfirm(true);
        }
    }, []);

    const handleKeepEditing = useCallback(() => {
        setShowCloseConfirm(false);
    }, []);

    const handleCloseAnyway = useCallback(() => {
        setShowCloseConfirm(false);
        onClose();
    }, [onClose]);

    const handleSaveAndClose = useCallback(async () => {
        setIsSaving(true);
        const saveAndClose = async () => {
            const didSave = await contentRef.current?.save();
            if (didSave) {
                setShowCloseConfirm(false);
                onClose();
            }
        };
        await saveAndClose().finally(() => {
            setIsSaving(false);
        });
    }, [onClose]);

    const header = (
        <TemplateDrawerTrail
            title="Edit template"
            templateName={template?.name}
        />
    );

    return (
        <>
            <Drawer
                open={isOpen}
                onClose={onClose}
                onCloseAttempt={handleCloseAttempt}
                direction="right"
                size="fullScreen"
                noPadding
                headerAddonLeft={header}
            >
                <div className={styles.drawerBody}>
                    <ChartOfAccountsTemplateStepSubtitle>
                        Review and edit accounts before saving
                    </ChartOfAccountsTemplateStepSubtitle>
                    <div className={styles.drawerContentFill}>
                        {isOpen && template && data ? (
                            <ChartOfAccountsTemplateEditorContent
                                ref={contentRef}
                                key={template.id}
                                template={template}
                                serverData={data}
                                onRequestClose={onClose}
                            />
                        ) : (
                            <Loader />
                        )}
                    </div>
                </div>
            </Drawer>

            <PlainModal show={showCloseConfirm} onHide={handleKeepEditing}>
                <StandardModalHeader>
                    Save changes before closing?
                </StandardModalHeader>
                <p>You have unsaved changes to this template.</p>
                <HStack as={StandardModalFooter} gap={8} justify="end">
                    <Button
                        variant="secondary"
                        size="xl"
                        onClick={handleCloseAnyway}
                    >
                        Close anyway
                    </Button>
                    <Tooltip content={saveBlockedReason}>
                        <Button
                            variant="primary"
                            size="xl"
                            onClick={handleSaveAndClose}
                            disabled={
                                isSaving || saveBlockedReason !== undefined
                            }
                        >
                            Save and close
                        </Button>
                    </Tooltip>
                </HStack>
            </PlainModal>
        </>
    );
};
